import api from "@/services/api";
import { useQuery } from "@tanstack/vue-query";
import { reactive, computed, toValue, type MaybeRefOrGetter } from "vue";
import { useAuthStore } from "@/store/auth";
import {
  useAuthedActiveOrganizationId,
  useHasManagerCapability,
} from "@/services/user";
import { formatNumber } from "@/utils/filters/numbers";

// credits fields are DRF-serialized Decimals: strings like "12.7500".
export interface FeatureTokenUsage {
  feature: string;
  tokensUsed: number;
  creditsUsed: string;
}

export interface UserTokenUsage {
  userId: number;
  userName: string;
  tokensUsed: number;
  creditsUsed: string;
  featureUsage: FeatureTokenUsage[];
}

export interface AITokenUsage {
  currentMonthUsage: number;
  monthlyLimit: number;
  isUnlimited: boolean;
  daysUntilRenewal: number;
  // The number the backend enforcement gate compares, and the number
  // featureUsage sums exactly to (see backend/ai_agents/pricing.py
  // credits_for_source — the view derives this by summing the same per-source
  // helper the gate calls, so no rounding gap exists between headline and rows).
  currentMonthCreditsUsed: string;
  monthlyCreditAllowance: number | null; // null = unlimited
  featureUsage: FeatureTokenUsage[];
  userUsage: UserTokenUsage[];
}

export interface AIUsageSummary {
  used: number;
  limit: number | null; // null = unlimited
  isUnlimited: boolean;
  isExceeded: boolean;
}

// The payload still carries the legacy token fields (currentMonthUsage,
// monthlyLimit), but the backend gate ignores them and compares credits, so
// every limit surface reads this instead. An unlimited organization
// (isUnlimited) gets a null allowance, so it needs no field of its own here.
export function creditUsageSummary(
  data: AITokenUsage | undefined,
): AIUsageSummary | undefined {
  if (!data) return undefined;
  const used = Number(data.currentMonthCreditsUsed);
  const limit = data.monthlyCreditAllowance;
  return {
    used,
    limit,
    isUnlimited: limit === null,
    // `>=` matches the backend gate (an allowance of 0 blocks a zero-usage
    // org — the admin kill-switch).
    isExceeded: limit !== null && used >= limit,
  };
}

export function sortAIUsageByCredits<T extends { creditsUsed: string }>(
  rows: readonly T[],
): T[] {
  return [...rows].sort(
    (a, b) => Number(b.creditsUsed) - Number(a.creditsUsed),
  );
}

export function aiUsageProgressBarConfig(
  summary: AIUsageSummary | undefined,
): { percentage: number; color: string }[] {
  if (!summary) return [];

  const { used, limit, isUnlimited } = summary;
  const percentage =
    isUnlimited || limit === null
      ? 0
      : limit === 0
        ? 100
        : Math.min((used / limit) * 100, 100);
  const color =
    percentage >= 90
      ? "bg-red-500"
      : percentage >= 70
        ? "bg-orange-400"
        : "bg-purple-500";

  return [{ percentage, color }];
}

// Mirrors the backend gate (token_limits.get_ai_token_limit_exceeded), so AI
// features are only disabled here when the backend would actually block.
export function isUsageLimitExceeded(data: AITokenUsage | undefined): boolean {
  return creditUsageSummary(data)?.isExceeded ?? false;
}

export const aiTokenUsageKeys = {
  all: ["aiTokenUsage"] as const,
  usage: (params: { organizationId?: number } = {}) =>
    [...aiTokenUsageKeys.all, params] as const,
};

function getAITokenUsage(
  organizationId: number,
  signal?: AbortSignal,
): Promise<AITokenUsage> {
  // The cache key says which organization the entry holds, so the request has
  // to say it too. Scope is resolved server-side from the session's selected
  // organization, which another tab — or this one — can move while the
  // request is in flight; the response names no organization, so nothing
  // would notice B's payload landing under A's key. The endpoint answers 409
  // rather than answering for someone else.
  return api
    .get("/ai-agents/token-usage/", {
      params: { organization: organizationId },
      signal,
    })
    .then((response) => response.data);
}

export function useAITokenUsageQuery({
  enabled = true,
}: {
  enabled?: MaybeRefOrGetter<boolean>;
} = {}) {
  const authStore = useAuthStore();
  // Organization-scoped, like consentKeys.list. Switching organizations only
  // INVALIDATES non-stable keys; invalidation refetches active observers but
  // never evicts data, and an observer whose `enabled` is false is not
  // refetched. On one flat key, a user who manages A and then switches to B
  // as a member keeps reading A's payload through the now-disabled observer —
  // which is exactly what a caller checking "do we have a payload?" treats as
  // an answer about B.
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useQuery({
      queryKey: computed(() =>
        aiTokenUsageKeys.usage({ organizationId: organizationId.value }),
      ),
      // From the KEY, not the live ref: a refetch of a cached entry (e.g.
      // refetchQueries({ type: "inactive" })) would otherwise fetch the
      // CURRENT organization and write it under the OLD key — the corruption
      // the 409 exists to prevent, except request and session agree so the
      // backend answers 200. `enabled` gates on the id being resolved, so
      // the key always carries one by the time this runs.
      queryFn: ({ queryKey, signal }) =>
        getAITokenUsage(queryKey[1].organizationId as number, signal),
      // Also waits for the organization id. Without it the query would run
      // once under a placeholder `undefined` key and then again under the
      // real one — two fetches, and consumers remounting underneath the key
      // change.
      enabled: computed(
        () =>
          authStore.isAuthed &&
          organizationId.value !== undefined &&
          toValue(enabled),
      ),
    }),
  );
}

// /ai-agents/token-usage/ is IsManager-gated, so every non-manager mounting a
// surface that checks the limit bought a guaranteed 403 — silent (api.ts
// carves it out of auto-logout) but real, and one ungated observer also
// populates the shared cache entry the gated observers read.
//
// A member therefore no longer learns their organization is over its limit.
// That warning was already only ever delivered by a 403 that falls back to
// `false`, so nothing that worked stops working. The real fix is a
// member-accessible credit-limit endpoint, tracked as a follow-up.
export function useIsAITokenLimitExceeded() {
  const usage = useAITokenUsageQuery({ enabled: useHasManagerCapability() });
  const isExceeded = computed(() => isUsageLimitExceeded(usage.data));
  return { isExceeded, usage };
}

// One credit formatter for the whole app so a display change (sub-cent floor,
// locale rule, "-" for null) can't diverge between the settings panel, the
// chat header, and any future surface. The DRF payload gives us "12.7500" as
// a string; Number(undefined) is NaN, which we surface as "0" so a
// frontend-ahead-of-backend deploy shows something sane.
export function formatCredits(
  value: string | number | null | undefined,
): string {
  if (value === null || value === undefined || value === "") return "0";
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(n)) return "0";
  // A row valued under a cent but above zero would floor to "0" against a
  // non-zero Total — misread as "no charge". Reserve "0" for genuine zeros.
  if (n > 0 && n < 0.01) return "<0.01";
  return formatNumber(n);
}
